import reporsHelpers from '../helpers';
import constants from '$$helpers/constants';

export default {
  calculateYAxisIntervals(leftAxisValues, rightAxisValues) {
    const maxLeftValue = leftAxisValues.reduce((max, value) => (+value > max ? +value : max), 0);
    const maxRightValue = rightAxisValues.reduce((max, value) => (+value > max ? +value : max), 0);

    const leftInterval = maxLeftValue / 6;
    const rightInterval = maxRightValue / 6;

    return {
      left: leftInterval,
      right: rightInterval,
    };
  },

  truncateTextToTwoLinesWithNewline(text, maxWidth, font = '14px Lato') {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    ctx.font = font;

    const words = text.split(' ');
    const firstLineWords = [];
    let currentLine = '';

    for (let i = 0; i < words.length; i++) {
      const testLine = currentLine ? `${currentLine} ${words[i]}` : words[i];
      const width = ctx.measureText(testLine).width;

      if (i === 0 && ctx.measureText(words[i]).width > maxWidth) {
        const chars = words[i].split('');
        let firstPart = '';
        let secondPart = '';

        for (let j = 0; j < chars.length; j++) {
          const testFirst = firstPart + chars[j];

          if (ctx.measureText(testFirst).width < maxWidth) {
            firstPart = testFirst;
          } else {
            secondPart = chars.slice(j).join('');
            break;
          }
        }

        const restWords = words.slice(1).join(' ');

        return `${firstPart}\n${secondPart} ${restWords}`.trim();
      }

      if (width < maxWidth) {
        currentLine = testLine;
        firstLineWords.push(words[i]);
      } else {
        break;
      }
    }

    const secondLineWords = words.slice(firstLineWords.length);

    return `${firstLineWords.join(' ')}\n${secondLineWords.join(' ')}`.trim();
  },

  prepareChartItemWithCalculatedValue(config) {
    const { item, value, hasLabel, color, type } = config;
    let formattedValue = 0;

    if (type === 'cost') {
      formattedValue = reporsHelpers.getFormattedBudgetValue(value);
    }

    if (type === 'time') {
      formattedValue = reporsHelpers.getTextValue(value);
    }

    const label = {
      formatter: formattedValue,
      position: 'insideTop',
    };

    return {
      ...item,
      ...(color ? { color } : {}),
      data: [
        ...item.data,
        {
          value,
          ...(hasLabel ? { label } : {}),
          tooltipInfo: {
            label: `${item.locale}_tooltip`,
            // value: value && typeof value === 'number' ? value.toFixed(2) : +value || 0,
            value: formattedValue,
          },
        },
      ],
    };
  },

  createChartTooltipForReportsByMonth(tooltipData, calculationMode, reportType) {
    const bars = tooltipData.filter(item => item.componentSubType === 'bar');
    const lines = tooltipData.filter(item => item.componentSubType === 'line');

    const tooltip = {}
    
    if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
      tooltip.bar1 = bars.find(b => ['totalCost', 'estimation'].includes(b?.seriesName));
      tooltip.bar2 = bars.find(b => ['actualCost', 'logs'].includes(b?.seriesName));

      if (bars.length === 2) {
        tooltip.barVarianceValue = tooltip?.bar1?.value - tooltip.bar2?.value;
      } else {
        tooltip.barVarianceValue = null;
      }

      tooltip.line1 = lines.find(b => ['cumTotalCost', 'cumulativeEstimation'].includes(b?.seriesName));
      tooltip.line2 = lines.find(b => ['cumActualCost', 'cumulativeLogs'].includes(b?.seriesName));

      if (lines.length === 2) {
        tooltip.lineVarianceValue = tooltip?.line1?.value - tooltip.line2?.value;
      } else {
        tooltip.lineVarianceValue = null;
      }
    }

    if (calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
      tooltip.bar1 = bars.find(b => ['totalBaselineCost', 'baselineEstimation'].includes(b?.seriesName));
      tooltip.bar2 = bars.find(b => ['actualCost', 'logs'].includes(b?.seriesName));

      if (bars.length === 2) {
        tooltip.barVarianceValue = tooltip?.bar1?.value - tooltip.bar2?.value;
      } else {
        tooltip.barVarianceValue = null;
      }

      tooltip.line1 = lines.find(b => ['cumBaselineTotalCost', 'cumulativeBaselineEstimation'].includes(b?.seriesName));
      tooltip.line2 = lines.find(b => ['cumActualCost', 'cumulativeLogs'].includes(b?.seriesName));

      if (lines.length === 2) {
        tooltip.lineVarianceValue = tooltip?.line1?.value - tooltip.line2?.value;
      } else {
        tooltip.lineVarianceValue = null;
      }
    }

    if (calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
      tooltip.bar1 = bars.find(b => ['totalBaselineCost', 'baselineEstimation'].includes(b?.seriesName));
      tooltip.bar2 = bars.find(b => ['totalCost', 'estimation'].includes(b?.seriesName));

      if (bars.length === 2) {
        tooltip.barVarianceValue = tooltip?.bar1?.value - tooltip.bar2?.value;
      } else {
        tooltip.barVarianceValue = null;
      }

      tooltip.line1 = lines.find(b => ['cumBaselineTotalCost', 'cumulativeBaselineEstimation'].includes(b?.seriesName));
      tooltip.line2 = lines.find(b => ['cumTotalCost', 'cumulativeEstimation'].includes(b?.seriesName));

      if (lines.length === 2) {
        tooltip.lineVarianceValue = tooltip?.line1?.value - tooltip.line2?.value;
      } else {
        tooltip.lineVarianceValue = null;
      }
    }

    if (tooltip.barVarianceValue) {
      if (reportType === 'cost') {
        tooltip.barVarianceValue = reporsHelpers.getFormattedBudgetValue(tooltip.barVarianceValue)
      }

      if (reportType === 'time') {
        tooltip.barVarianceValue = reporsHelpers.getTextValue(tooltip.barVarianceValue)
      }
    }
    if (tooltip.lineVarianceValue) {
      if (reportType === 'cost') {
        tooltip.lineVarianceValue = reporsHelpers.getFormattedBudgetValue(tooltip.lineVarianceValue)
      }

      if (reportType === 'time') {
        tooltip.lineVarianceValue = reporsHelpers.getTextValue(tooltip.lineVarianceValue)
      }
    }

    const axisValueLabel = bars[0]?.axisValueLabel || lines[0]?.axisValueLabel;
    const values = axisValueLabel.split(' ');

    return `<div class="chart_tooltip">
            <div class="tooltip_title">${__(`${values[0]}_full`)} ${values[1]}</div>
            ${tooltip?.bar1 !== undefined ? `<div class="tooltip_info">
              <div class="marker">${tooltip.bar1.marker}</div>
              <div class="name">${__(`${tooltip.bar1.data.tooltipInfo.label}`)}</div>
              <div class="value">${tooltip.bar1.data.tooltipInfo.value}</div>
            </div>` : ''}
            ${tooltip?.bar2 !== undefined ? `<div class="tooltip_info">
              <div class="marker">${tooltip.bar2.marker}</div>
              <div class="name">${__(`${tooltip.bar2.data.tooltipInfo.label}`)}</div>
              <div class="value">${tooltip.bar2.data.tooltipInfo.value}</div>
            </div>` : ''}
            ${tooltip.barVarianceValue ? `<div class="tooltip_info margin-bottom-8">
              <div class="name">${__('report_page_variance')}</div>
              <div class="value">${tooltip.barVarianceValue}</div>
            </div>` : ''}

            ${tooltip?.line1 !== undefined ? `<div class="tooltip_info">
              <div class="marker">${tooltip.line1.marker}</div>
              <div class="name">${__(`${tooltip.line1.data.tooltipInfo.label}`)}</div>
              <div class="value">${tooltip.line1.data.tooltipInfo.value}</div>
            </div>` : ''}
            ${tooltip?.line2 !== undefined ? `<div class="tooltip_info">
              <div class="marker">${tooltip.line2.marker}</div>
              <div class="name">${__(`${tooltip.line2.data.tooltipInfo.label}`)}</div>
              <div class="value">${tooltip.line2.data.tooltipInfo.value}</div>
            </div>` : ''}
            ${tooltip.lineVarianceValue ? `<div class="tooltip_info margin-bottom-8">
              <div class="name">${__('report_page_variance_cumulative')}</div>
              <div class="value">${tooltip.lineVarianceValue}</div>
            </div>` : ''}
          </div>`;
  },
};